import { ProductLineProps } from 'components/Blocks/ProductLine/ProductLine';
import { ProductLineSkeleton } from 'components/Blocks/ProductLine/ProductLineSkeleton';
import { useIntersection } from 'hooks/ui/useIntersection';
import dynamic from 'next/dynamic';
import { RefObject, useCallback, useEffect, useState } from 'react';

type ProductLineWrapperProps = {
    parentRef: RefObject<HTMLDivElement>;
    increaseParentHeightCounter?: () => void;
} & ProductLineProps;

const ProductLine = dynamic<ProductLineProps>(
    () => import('components/Blocks/ProductLine/ProductLine').then((component) => component.ProductLine),
    {
        loading: () => <ProductLineSkeleton />,
        ssr: false,
    },
);

export const ProductLineWrapper: FC<ProductLineWrapperProps> = ({
    parentRef,
    brand,
    productDetailUuid,
    increaseParentHeightCounter,
}: ProductLineWrapperProps) => {
    const intersection = useIntersection(parentRef);
    const [intersectedAtLeastOnce, setIntersectedAtLeastOnce] = useState(false);

    /*
     * Trigger collapse height calculation when layout changes
     * (otherwise the collapsed block may not be fully displayed)
     */
    const containerRef = useCallback(
        (node: Element | null) => {
            if (!node || !increaseParentHeightCounter) {
                return undefined;
            }

            let lastHeight = node.clientHeight;

            const resizeObserver = new ResizeObserver(() => {
                const newHeight = node.clientHeight;
                if (newHeight !== lastHeight) {
                    increaseParentHeightCounter();
                    lastHeight = newHeight;
                }
            });

            resizeObserver.observe(node);

            return undefined;
        },
        [increaseParentHeightCounter],
    );

    useEffect(() => {
        if (intersection?.isIntersecting) {
            setIntersectedAtLeastOnce(true);
        }
    }, [intersection?.isIntersecting]);

    if (!intersectedAtLeastOnce) {
        return <ProductLineSkeleton />;
    }

    return (
        <div ref={containerRef}>
            <ProductLine brand={brand} productDetailUuid={productDetailUuid} />
        </div>
    );
};
